
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Hammer, MessageSquareText } from "lucide-react";

type Feature = {
  id: "mcp" | "prompts";
  title: string;
  description: string;
  content: React.ReactNode;
  icon: React.ReactNode;
};

const features: Feature[] = [{
  id: "mcp",
  title: "MCP integration",
  description: "Simple setup that connects CodeRide to your AI agent. Once configured, your agent gains <strong className=\"font-semibold text-gray-900\">persistent memory</strong> of your entire project structure and context.",
  content: <pre className="bg-[#1E1E1E] p-4 rounded-lg text-sm text-white font-mono overflow-x-auto">
      <code className="block whitespace-pre-wrap">
        <span className="text-blue-400">{"{"}</span>
        <span className="block pl-4">
          <span className="text-green-400">"mcpServers"</span>: {"{"}
          <span className="block pl-4">
            <span className="text-green-400">"coderide"</span>: {"{"}
            <span className="block pl-4">
              <span className="text-green-400">"command"</span>: <span className="text-yellow-400">"npx"</span>,
              <span className="block"><span className="text-green-400">"args"</span>: [</span>
              <span className="block pl-4">
                <span className="text-yellow-400">"-y"</span>,
                <span className="text-yellow-400">"@coderide/coderide-mcp"</span>
              </span>
              <span className="block">],</span>
              <span className="block"><span className="text-green-400">"env"</span>: {"{"}</span>
              <span className="block pl-4">
                <span className="text-green-400">"CODERIDE_API_KEY"</span>: <span className="text-yellow-400">"&lt;YOUR_CODERIDE_API_KEY&gt;"</span>
              </span>
              <span className="block">{"}"}</span>
            </span>
            <span className="block">{"}"}</span>
          </span>
          <span className="block">{"}"}</span>
        </span>
        <span className="text-blue-400">{"}"}</span>
      </code>
    </pre>,
  icon: <Hammer className="w-6 h-6 text-primary" />
}, {
  id: "prompts",
  title: "Auto-generated prompts",
  description: "Every task comes pre-loaded with project context and optimized instructions. Your AI agent gets the <strong className=\"font-semibold text-gray-900\">full picture</strong> without repetitive explanations.",
  content: <pre className="bg-[#1E1E1E] p-4 rounded-lg text-sm text-white font-mono overflow-x-auto">
      <code className="block whitespace-pre-wrap">
        <span className="text-blue-400">/**</span>
        <span className="text-blue-400 block">ENVIRONMENT CONFIGURATION TASK:</span> 
        <span className="text-gray-400 block">This is the FIRST task in the backend-proxied implementation.</span>
        <span className="text-gray-400 block">Focus on secure, validated TypeScript environment configuration.</span>
        <span className="text-blue-400 block">*/</span>
        
        <span className="text-purple-400 block mt-4">/** CONTEXT PROCESSING (REQUIRED): */</span>
        <span className="text-gray-400 block">After retrieving project and task data, respond with:</span>
        <span className="text-green-400 block">- [PROJECT_STRUCTURE_ANALYZED]</span>
        <span className="text-gray-400 block pl-2">with 3 key observations about the environment configuration requirements</span>
        <span className="text-green-400 block">- [CODE_PATTERNS_IDENTIFIED]</span>
        <span className="text-gray-400 block pl-2">with TypeScript patterns for environment validation you{"'"}ll follow</span>
        <span className="text-green-400 block">- [TASK_REQUIREMENTS_EXTRACTED]</span>
        <span className="text-gray-400 block pl-2">with numbered list of all environment configuration requirements</span>
        
      </code>
    </pre>,
  icon: <MessageSquareText className="w-6 h-6 text-primary" />
}];

const FeatureShowcaseSection = () => {
  const [hoveredFeature, setHoveredFeature] = useState<Feature["id"] | null>(features[0].id);
  return <section className="section-padding bg-white relative">
      <div className="container relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 md:text-3xl">
            Stop Losing Context Between Sessions
          </h2>
          <p className="text-center text-muted-foreground mt-4 max-w-3xl mx-auto text-base">
            CodeRide's <span className="bg-[#7867EE] text-white px-2 py-1 rounded font-mono">MCP</span> integration and <span className="bg-[#7867EE] text-white px-2 py-1 rounded font-mono">context optimization</span> ensure your AI agent <strong className="font-semibold text-gray-900">remembers your project architecture</strong>, eliminating the need to rebuild understanding from scratch.{" "}
            <button 
              onClick={() => window.open('https://app.coderide.ai/auth/sign-up', '_blank')}
              className="inline-flex items-center text-base font-bold text-coderide-light-blue hover:text-coderide-dark-blue transition-colors"
            >
              <span className="underline">Experience it now</span> →
            </button>
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-8">
            {features.map(feature => <div key={feature.id} className="group cursor-pointer bg-gray-50 rounded-xl p-6 transition-all duration-300 hover:shadow-md" onMouseEnter={() => setHoveredFeature(feature.id)}>
                <div className="flex items-center gap-4 mb-3">
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    {feature.icon}
                  </div>
                  <h3 className={`text-xl font-medium transition-colors duration-300 ${hoveredFeature === feature.id ? "text-primary" : "text-gray-900"}`}>
                    {feature.title}
                  </h3>
                </div>
                
                <div 
                  className={`pl-16 text-gray-600 transition-opacity duration-300 ${hoveredFeature === feature.id ? "opacity-100" : "opacity-70"}`}
                  dangerouslySetInnerHTML={{ __html: feature.description }}
                />
              </div>)}
          </div>

          <div className="relative">
            <div className="rounded-xl overflow-hidden shadow-lg bg-[#1E1E1E] p-3">
              <div className="flex items-center gap-1 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <div className="max-w-full overflow-x-auto">
                {features.find(f => f.id === hoveredFeature)?.content}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};

export default FeatureShowcaseSection;
